import React, { useCallback, useEffect, useState } from "react";

import moment from "moment";

import { addVerificationFarmTask, getAllVerificationTasks } from "services";

import { getAdminVerificationDeadline, getAdminVerificationYearlyDataYear, getEnvironment, useAdminUserContext, useCancelAsyncOnUnmount } from "utils";

import { Button, DatePicker, Drawer, Form, Option, Select, Typography } from "components";

export const CustomTaskDrawer = ({ farmCountry, visible, onClose, farmId }) => {
  const [verificationTasks, setVerificationTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form] = Form.useForm();

  const year = getAdminVerificationYearlyDataYear();

  const adminUser = useAdminUserContext();
  const adminVerificationDeadline = getAdminVerificationDeadline();

  const signal = useCancelAsyncOnUnmount()();

  const env = getEnvironment();

  const addFarmTask = useCallback(() => {
    setLoading(true);
    const formValues = form.getFieldsValue();
    addVerificationFarmTask({
      signal,
      ...formValues,
      deadline: formValues.deadline.endOf("day"),
      farmId,
      adminId: adminUser.user.id,
    })
      .then(() => onClose())
      .finally(() => setLoading(false));
  }, [signal, farmId, form, onClose, adminUser]);

  const loadVerificationTasks = useCallback(() => {
    setLoading(true);
    getAllVerificationTasks({
      signal,
    })
      .then((res) => setVerificationTasks(res.tasks))
      .finally(() => setLoading(false));
  }, [signal]);

  useEffect(() => {
    loadVerificationTasks();
  }, [loadVerificationTasks]);

  return (
    <Drawer
      footer={
        <Button type="primary" onClick={() => form.submit()} loading={loading}>
          Save and send
        </Button>
      }
      loading={loading}
      onClose={onClose}
      title="Create a custom task"
      visible={visible}
    >
      <Typography variant="h2" className="margin-bottom-100">
        Create custom verification task for a farmer
      </Typography>
      <Typography variant="paragraph-m">
        To request additional documents necessary for verification process create custom verification tasks and send it to the farmer.
      </Typography>
      <Typography variant="h4" className="margin-top-300 margin-bottom-100">
        Document details
      </Typography>
      <Typography variant="paragraph-m">
        Select one task from the list of ready templates. If you can't find document you want to request then go to verification settings and create new task
        template. After adding new task template it should be visible in this form.
      </Typography>
      <Form form={form} onFinish={addFarmTask}>
        <Select className="margin-top-200" label="Select task" name="taskId" rules={[{ required: true }]}>
          {verificationTasks
            .filter((task) => task.country === farmCountry && !task.disabled)
            .map(({ documentName, id }) => (
              <Option key={id} value={id}>
                {documentName}
              </Option>
            ))}
        </Select>
        <Select className="margin-top-100" label="Document year" name="year" rules={[{ required: true }]}>
          {Array.from({ length: 6 }, (_, i) => year - i).map((year, i) => (
            <Option key={i} value={year}>
              {year}
            </Option>
          ))}
        </Select>
        <DatePicker
          className="margin-top-100"
          // In local, dev and staging we want the admin to chose any date for testing purposes
          disabledDate={env.local || env.development || env.staging ? null : (v) => v && (v <= moment().startOf("day") || v > adminVerificationDeadline)}
          format={["DD.MM.YYYY"]}
          label="Upload deadline"
          name="deadline"
          rules={[{ required: true }]}
          showNow
        />
      </Form>
    </Drawer>
  );
};
